import { css } from '@emotion/react'
import { graphql } from 'gatsby'
import { type ComponentProps, useState } from 'react'

import { Carousel } from '@/features/carousel'
import { DatoStructuredText } from '@/features/common/'
import { mq, widthInCols } from '@/theme/mixins'

import { LandingTestimonialSlide } from './LandingTestimonialSlide'

interface Props extends ComponentProps<'section'> {
  data: Queries.LandingTestimonialsFragment | null
}

export const LandingTestimonials = ({ data, ...props }: Props) => {
  const [navRef, setNavRef] = useState<HTMLElement | null>(null)

  const styles = {
    section: css`
      display: grid;
      grid-template-columns: auto 1fr;
      color: #fff;
      padding: var(--row-72) 0 var(--row-72);
      ${mq().s} {
        padding: var(--row-108) 0 var(--row-144);
      }
    `,
    heading: css`
      grid-column: 1 / 3;
      margin: 0 var(--margin);
      font-size: var(--fs-60);
      display: flex;
      align-items: flex-end;
      gap: 0.25em;
      &:after {
        display: block;
        content: '';
        flex: 1;
        width: 100%;
        height: 2px;
        background: #fff;
        margin-bottom: 0.25em;
      }
    `,
    body: css`
      grid-column: 1 / 2;
      margin-left: var(--margin);
      margin-bottom: 1.5em;
      font-weight: 350;
      max-width: 48ch;
    `,
    nav: css`
      grid-column: 2 / 3;
      display: flex;
      justify-content: flex-end;
      margin-right: calc(var(--margin) - 1em);
      margin-top: 1em;
      margin-bottom: 1.5em;
      --arrow-color: #fff;
      --arrow-color-hover: #000;
    `,
    slider: css`
      grid-column: 1 / 3;
      --scroll-width: calc(
        ${widthInCols({ count: 9 })} + var(--gtr-36)
      );
      ${mq().ms} {
        --scroll-width: calc(
          ${widthInCols({ count: 11 })} + var(--gtr-36)
        );
      }
    `,
    sliderContent: css`
      display: grid;
      --gtr: var(--gtr-18);
      grid-column-gap: var(--gtr);
      grid-template-columns: repeat(
        ${data?.testimonials?.length},
        ${widthInCols({ count: 9 })}
      );
      ${mq().ms} {
        grid-template-columns: repeat(
          ${data?.testimonials?.length},
          ${widthInCols({ count: 11 })}
        );
      }
    `,
  }
  return (
    <section
      css={styles.section}
      id="testimonials"
      {...props}
    >
      <h2 css={styles.heading}>
        <span>{data?.heading}</span>
      </h2>
      <div css={styles.body}>
        <DatoStructuredText data={data?.body} />
      </div>
      <nav
        ref={node => setNavRef(node)}
        css={styles.nav}
      />
      <Carousel
        css={styles.slider}
        contentCss={styles.sliderContent}
        navContainer={navRef}
        snap
        navVariant="ABOVE"
      >
        {data?.testimonials?.map((testimonial, i) => (
          <LandingTestimonialSlide
            data={testimonial}
            key={i}
          />
        ))}
      </Carousel>
    </section>
  )
}

export const LandingTestimonialsFragment = graphql`
  fragment LandingTestimonials on DatoCmsLandingTestimonialCarousel {
    __typename
    id: originalId
    heading
    body {
      value
    }
    testimonials {
      ...LandingTestimonialSlide
    }
  }
`
